<template>
    <div :class="cardClass">
        <template v-if="!isLoading">
            <div :class="statusClass"></div>
            <div class="RPCompactReviewCard__content">
                <div class="RPCompactReviewCard__header-bar">
                    <div class="RPCompactReviewCard__header">
                        <span class="RPCompactReviewCard__location">{{ location.display_name }}</span>
                        <div class="RPCompactReviewCard__site">
                            <RPSiteBadge
                                :name="profile.site.name"
                                class="RPCompactReviewCard__badge"
                            />
                            <span class="RPCompactReviewCard__site-name">{{ profile.site.name }}</span>
                        </div>
                    </div>
                    <RPResponsePill
                        :status="responseStatus.toLowerCase()"
                    />
                </div>
                <div class="RPCompactReviewCard__main-container">
                    <div class="RPCompactReviewCard__avatar-container">
                        <img
                            class="RPCompactReviewCard__user-avatar"
                            :src="review.user_profile_image"
                            alt="user_image"
                        >
                    </div>
                    <div class="RPCompactReviewCard__review-container">
                        <div class="RPCompactReviewCard__user-container">
                            <span class="RPCompactReviewCard__user-name">{{ userName }}</span>
                            <span class="RPCompactReviewCard__time">{{ timeSince }}</span>
                        </div>
                        <RPStarCounter
                            class="RPCompactReviewCard__stars"
                            :rating="review.rating"
                            :status="cardStatus"
                            :show-count-badge="false"
                        />
                        <div class="RPCompactReviewCard__review-text">
                            {{ reviewText }}
                        </div>
                    </div>
                </div>
            </div>
        </template>
    </div>
</template>
<script>
import RPResponsePill from 'components/Complex/Pills/RPResponsePill.vue';
import RPSiteBadge from 'components/Basic/Misc/RPSiteBadge';
import statusBuilder from 'status/statusBuilder';
import stat from 'status/index';
import has from 'lodash/has';
import responseStatuses from './ResponseCard/responseStatuses';
import RPStarCounter from 'components/Basic/Misc/RPStarCounter.vue';
import moment from 'moment';

const reviewValidator = (r) => {
    return has(r, 'id')
        && has(r, 'rating')
        && has(r, 'review')
        && has(r, 'reviewer')
        && has(r, 'review_date')
        && has(r, 'review_time')
        && has(r, 'user_profile_image')
        && has(r, 'response')
        && has(r, ['location', 'display_name'])
        && has(r, ['profile', 'id'])
        && has(r, ['profile', 'site','name']);
};

export default {
    name: 'RPCompactReviewCard',
    components: {
        RPSiteBadge,
        RPResponsePill,
        RPStarCounter
    },
    props: {
        // Basic Required Data
        review: {
            type: Object,
            required: true,
            validator: reviewValidator,
        },

        // Specific UI Controls
        isThreeStarsError: {
            type: Boolean,
            default: false
        },

        //Status
        status: {
            type: String,
            default: stat.STANDBY,
        },

    },
    computed: {
        cardStatus(){
            switch(this.review.rating) {
            case 5:
            case 4:
                return stat.SUCCESS;
            case 3:
                return this.isThreeStarsError ? stat.ERROR : stat.WARNING;
            default:
                return stat.ERROR;
            }
        },
        cardClass(){
            return statusBuilder('RPCompactReviewCard', this.cardStatus);
        },
        statusClass(){
            return statusBuilder('RPCompactReviewCard__status', this.cardStatus);
        },
        isLoading(){
            return this.status === stat.LOADING;
        },
        responseStatus(){
            return this.review.response ? this.review.response.status : responseStatuses.UNPUBLISHED;
        },
        userName(){
            return this.review.reviewer === ''? 'Anonymous' : this.review.reviewer;
        },
        timeSince(){
            //Days Calculation
            const dateDiff = moment().diff(moment(this.review.review_date, 'YYYY-MM-DD'), 'days');
            if(dateDiff > 0) {
                return `${dateDiff} ${dateDiff > 1 ? 'days' : 'day'} ago`;
            }
            //Hours Calculation
            const reviewTime = moment(`${this.review.review_date} ${this.review.review_time}`, 'YYYY-MM-DD HH:mm:ss');
            const hoursDiff = moment().diff(reviewTime, 'hours');
            if(hoursDiff > 0) {
                return `${hoursDiff} ${hoursDiff > 1 ? 'hrs' : 'hr'} ago`;
            }
            //Minutes Calculation
            const minDiff = moment().diff(reviewTime, 'minutes');
            if(minDiff > 0) {
                return `${minDiff} ${minDiff > 1 ? 'mins' : 'min'} ago`;
            }
            //Seconds Calculation
            const secDiff = moment().diff(reviewTime, 'seconds');
            return `${secDiff} ${secDiff > 1 ? 'seconds' : 'second'} ago`;
        },
        reviewText(){
            return this.review.review.length < 120
                ? this.review.review
                : this.review.review.substr(0, 120)+'...';
        },
    }
};
</script>
<style lang="scss">
.RPCompactReviewCard {
    background: $white;
    box-shadow: 0px 2px 5px rgba(0, 0, 0, 0.08);
    border-radius: 4px;
    display: flex;
    min-height: 5rem;

    &__status{
        display: block;
        border-top-left-radius: 4px;
        border-bottom-left-radius: 4px;
        width: .25rem;
        &--success { background-image: $gradient-positive; }
        &--warning { background-image: $gradient-yellow; }
        &--error { background-image: $gradient-negative; }
    }

    &__content {
        padding: 1rem;
        flex-grow: 1;
        display: flex;
        flex-direction: column;
    }

    &__header-bar {
        display: flex;
        align-items: center;
        margin-bottom: 0.5rem;
    }
    &__header {
        display: flex;
        align-items: center;
        flex-grow: 1;

    }
    &__location {
        color: $text-dark;
        font-weight: 600;
        margin-right: 0.5rem;
    }

    &__site {
        display: flex;
        align-items: center;
    }

    &__badge {
        width: 1rem;
        height: 1rem;
        margin-right: 0.15rem;
    }
    &__site-name {
        font-size: 0.75rem;
        color: $text-grey;
    }

    &__main-container {
        flex-grow: 1;
        display: flex;
    }
    &__avatar-container {
        margin-right: 0.5rem;
        height: 100%;
    }
    &__user-avatar{
        border-radius: 90%;
        height: 2rem;
        width: 2rem;
    }

    &__review-container {
        flex-grow: 1;
        display: flex;
        flex-direction: column;
    }
    &__user-container {
        margin-bottom: 0.25rem
    }
    &__user-name {
        color: $text-dark;
        font-weight: 600;
        margin-right:  0.25rem ;
    }
    &__time {
        color: $text-grey;
        font-size: 0.7rem;
    }
    &__stars {
        margin-bottom: 0.5rem;
    }
    &__review-text {
        color: $text-grey;
        font-size: 0.85rem;
    }
}
</style>
